import {
	Address,
	Profile,
	useUserCompareDataQuery,
	User,
} from '../../graphql/graphql';
import { NCZIPatientSummaryContactInformation } from '../../Modules/Nczi/Interfaces/ncziPatientInformations';
import { PatientDataVerificationVZPResultExtra } from '../../Modules/Patient/Components/DataVerification/VZP/PatientDataVerificationVZPResult';

interface CsszPatientData {
	address: Address;
	patient: Profile;
}

function mapUserData(user: Partial<User>) {
	return {
		salutation: user.profile?.salutation ?? '',
		salutationBefore: user.profile?.salutationBefore ?? '',
		firstName: user.profile?.firstName ?? '',
		lastName: user.profile?.lastName ?? '',
		salutationAfter: user.profile?.salutationAfter ?? '',
		'address.street': user.profile?.address?.street ?? '',
		'address.descriptiveNumber': user.profile?.address?.descriptiveNumber ?? '',
		'address.guideNumber': user.profile?.address?.guideNumber ?? '',
		'address.city': user.profile?.address?.city ?? '',
		'address.postCode': user.profile?.address?.postCode ?? '',
		insuranceNumber: user.profile?.insuranceNumber ?? '',
		insuranceCompanyCode: user.profile?.insuranceCompanyTermItem?.value ?? '',
	};
}

export type PatientDataCompareKey = keyof ReturnType<typeof mapUserData>;

export function mapApiPatientDataToCompareTable(
	data: ReturnType<typeof useUserCompareDataQuery>['data'],
) {
	const { originalUser, ...virtualUser } = data.user;
	return {
		virtual: mapUserData(virtualUser),
		original: mapUserData(originalUser),
	};
}

export function mapCsszPatientDataToCompareTable({
	address,
	patient,
}: CsszPatientData) {
	const apiLikeData = {
		profile: {
			...patient,
			address,
		},
	};

	return mapUserData(apiLikeData);
}

export function mapVzpPatientDataToCompareTable(
	data: Partial<PatientDataVerificationVZPResultExtra>,
) {
	return {
		insuranceCompanyCode: data?.insurance?.title
			? data.insurance.title.split(' ')[0]
			: '',
	};
}

export const mapNCZIDataToCompareTable = (
	patientBirthNumber: string,
	data: NCZIPatientSummaryContactInformation,
): Record<PatientDataCompareKey, string> => {
	const person = data.patientJRUZ?.person;
	const address = data.patientJRUZ?.address;
	const insurance = data.patientJRUZ?.insurance;

	return {
		salutationBefore: person?.salutationBefore ?? '',
		salutation: person?.salutationBefore ?? '',
		firstName: person?.firstName ?? '',
		lastName: person?.lastName ?? '',
		salutationAfter: person?.salutationAfter ?? '',
		'address.street': address?.street ?? '',
		'address.descriptiveNumber': address?.descriptiveNumber ?? '',
		'address.guideNumber': address?.guideNumber ?? '',
		'address.city': address?.city?.displayName ?? '',
		'address.postCode': (address?.postCode?.displayName ?? '').replace(
			/\s+/g,
			'',
		),
		insuranceNumber: patientBirthNumber,
		insuranceCompanyCode: insurance?.company?.code?.value ?? '',
	};
};
